import { useMemo, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { PlusIcon } from "@icons";
import { buildProductItems } from "./PickerPlaygroundProducts";
import { VariantGrid, VariantItem } from "./playgroundControls";
import styles from "./PickerPlayground.module.scss";

const useProductItems = (onAction: (action: string) => void) =>
    useMemo(
        () =>
            buildProductItems((product, query) => (
                <Picker.Item
                    leading={<PlusIcon />}
                    onSelect={() => onAction(`add price:${product}:${query}`)}
                >
                    {query.trim() === ""
                        ? "Add price"
                        : `Add "${query.trim()}"`}
                </Picker.Item>
            )),
        [onAction],
    );

const renderProductFooter =
    (onAction: (action: string) => void) => (query: string) => (
        <Picker.Item
            leading={<PlusIcon />}
            onSelect={() => onAction(`add product:${query}`)}
        >
            {query.trim() === "" ? "Add product" : `Add "${query.trim()}"`}
        </Picker.Item>
    );

/**
 * The invoice item / price picker that decided the flyout presentation: a
 * free-text input whose product rows open a searchable price level with an
 * "Add price" footer and the Select button-trigger version of the same data.
 * Open a row (hover, click, ArrowRight) to see the level fly out to the right.
 */
export const PickerFlyoutGrid = () => {
    const [lastAction, setLastAction] = useState<string | null>(null);
    const items = useProductItems(setLastAction);
    const [value, setValue] = useState<string | null>(null);

    return (
        <VariantGrid title="Flyout levels (cascading)" columns="wide">
            <VariantItem label="Input trigger · allowCustomValue · products with price flyouts, Add product footer">
                <div className={styles.flyoutCell}>
                    <Picker.Select
                        items={items}
                        trigger="input"
                        allowCustomValue
                        nested="flyout"
                        placeholder="Input"
                        clearable
                        renderFooter={renderProductFooter(setLastAction)}
                    />
                </div>
            </VariantItem>
            <VariantItem label="Select · button trigger, searchable, same levels">
                <div className={styles.flyoutCell}>
                    <Picker.Select
                        items={items}
                        nested="flyout"
                        searchable
                        searchPlaceholder="Search..."
                        placeholder="Select"
                        value={value}
                        onValueChange={setValue}
                        renderFooter={renderProductFooter(setLastAction)}
                    />
                </div>
            </VariantItem>
            <VariantItem label="Last action">
                <code>{lastAction ?? "none"}</code>
            </VariantItem>
        </VariantGrid>
    );
};
